import React, { createContext, useContext, useEffect, useMemo, useState } from "react"
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { buildAgentTheme, buildAppTheme, normalizeTheme, terminalOptionsFrom, themeCssVars } from "../../theme"

const AppearanceContext = createContext(normalizeTheme({}))
const STORAGE_KEY = "appearance_settings"

function loadAppearance() {
  try {
    return normalizeTheme(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}"))
  } catch {
    return normalizeTheme({})
  }
}

export function AppearanceProvider({ children }) {
  const [saved, setSaved] = useState(loadAppearance)
  // Unsaved tokens pushed by the theme editor so edits are visible while tweaking.
  const [preview, setPreview] = useState(null)
  const tokens = preview ?? saved

  useEffect(() => {
    const update = (event) => setSaved(normalizeTheme(event.detail ?? loadAppearance()))
    const previewUpdate = (event) => setPreview(event.detail ? normalizeTheme(event.detail) : null)
    window.addEventListener("appearance-settings-changed", update)
    window.addEventListener("appearance-preview-changed", previewUpdate)
    return () => {
      window.removeEventListener("appearance-settings-changed", update)
      window.removeEventListener("appearance-preview-changed", previewUpdate)
    }
  }, [])

  useEffect(() => {
    const root = document.documentElement
    Object.entries(themeCssVars(tokens)).forEach(([name, value]) => root.style.setProperty(name, value))
    root.dataset.reduceAnimations = tokens.effects.reduceAnimations ? "true" : "false"
    root.dataset.themeMode = tokens.mode
    root.style.colorScheme = tokens.mode
  }, [tokens])

  const theme = useMemo(() => buildAppTheme(tokens), [tokens])
  const value = useMemo(() => ({
    ...tokens,
    // Legacy flat accessors kept for existing consumers.
    accent: tokens.colors.accent,
    density: tokens.shape.density,
    reduceAnimations: tokens.effects.reduceAnimations,
    previewing: preview !== null,
  }), [preview, tokens])

  return (
    <AppearanceContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppearanceContext.Provider>
  )
}

export function useAppearance() {
  return useContext(AppearanceContext)
}

export function useAgentTheme(agent) {
  const tokens = useAppearance()
  return useMemo(() => buildAgentTheme(tokens, agent), [agent, tokens])
}

export function useTerminalOptions() {
  const tokens = useAppearance()
  return useMemo(() => terminalOptionsFrom(tokens), [tokens])
}

// Pushes/clears the live preview used by the theme editor.
export function setThemePreview(tokens) {
  window.dispatchEvent(new CustomEvent("appearance-preview-changed", { detail: tokens ?? null }))
}
